import { supabase } from "@/integrations/supabase/client";
import { attributionMetadata } from "@/lib/attribution";

const ANON_QUEUE_KEY = "greyrock_anon_events";
const ANON_SESSION_KEY = "gr_anon_session_id";

type AnonEvent = { event_name: string; metadata: Record<string, unknown>; ts: string };

// Durable per-device/session id so anonymous funnel events can be stitched together
// before (and after) a signup happens.
export const getAnonSessionId = (): string => {
  if (typeof window === "undefined") return "server";
  try {
    let id = localStorage.getItem(ANON_SESSION_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(ANON_SESSION_KEY, id);
    }
    return id;
  } catch {
    return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
};

const enqueueAnon = (event_name: string, metadata: Record<string, unknown>) => {
  try {
    const raw = localStorage.getItem(ANON_QUEUE_KEY);
    const arr: AnonEvent[] = raw ? JSON.parse(raw) : [];
    arr.push({ event_name, metadata, ts: new Date().toISOString() });
    // cap to 50 events to keep storage small
    localStorage.setItem(ANON_QUEUE_KEY, JSON.stringify(arr.slice(-50)));
  } catch {}
};

export const trackEvent = async (
  eventName: string,
  metadata: Record<string, unknown> = {}
) => {
  // Event-specific metadata wins on key conflicts.
  metadata = { ...attributionMetadata(), ...metadata };
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      // Write anonymous events straight to the database so the pre-signup funnel
      // is visible even if the visitor never creates an account.
      const session_id = getAnonSessionId();
      const { error } = await supabase.from("anonymous_events" as any).insert({
        session_id,
        event_name: eventName.slice(0, 100),
        metadata: metadata as any,
        path: typeof window !== "undefined" ? window.location.pathname.slice(0, 300) : null,
      });
      if (error) {
        // Network/RLS failure — keep a local copy so nothing is silently lost.
        enqueueAnon(eventName, metadata);
      }
      return;
    }

    await supabase.from("analytics_events" as any).insert({
      user_id: user.id,
      event_name: eventName,
      metadata: metadata as any,
    });
  } catch (error) {
    console.error("[Analytics] Failed to track event:", error);
  }
};

// Flush any anonymous events that failed to reach the database into the authenticated user's row.
export const flushAnonymousEvents = async () => {
  try {
    const raw = localStorage.getItem(ANON_QUEUE_KEY);
    if (!raw) return;
    const arr: AnonEvent[] = JSON.parse(raw);
    if (!arr.length) {
      localStorage.removeItem(ANON_QUEUE_KEY);
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const rows = arr.map((e) => ({
      user_id: user.id,
      event_name: e.event_name,
      metadata: { ...e.metadata, anon_captured_at: e.ts } as any,
    }));
    await supabase.from("analytics_events" as any).insert(rows);
    localStorage.removeItem(ANON_QUEUE_KEY);
  } catch (error) {
    console.error("[Analytics] Failed to flush anon events:", error);
  }
};

const LANDING_FLAG = "gr_session_landing_sent";

// One anonymous "session_landing" event per browser session, with the original URL.
export const trackSessionLanding = (pathname: string, search: string) => {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(LANDING_FLAG)) return;
    sessionStorage.setItem(LANDING_FLAG, "1");
  } catch {
    return;
  }
  const metadata = {
    ...attributionMetadata(),
    landing_path: pathname.slice(0, 300),
    landing_query: search.slice(0, 500),
    referrer: (document.referrer || "").slice(0, 300),
  };
  void supabase.from("anonymous_events" as any).insert({
    session_id: getAnonSessionId(),
    event_name: "session_landing",
    metadata: metadata as any,
    path: pathname.slice(0, 300),
  }).then(({ error }) => { if (error) enqueueAnon("session_landing", metadata); });
};
